import {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    InputHTMLAttributes,
    LabelHTMLAttributes,
    TextareaHTMLAttributes,
    useCallback,
    useRef,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { namespaces } from "../i18n.costants";
import { PrimaryButton } from "./Button";
import * as Dialog from '@radix-ui/react-dialog';
import { ReactComponent as X } from '../media/x.svg';
import { GeneralText, StyledHeader } from "./GeneralText";
import { useClickOutside } from "./UseClickOutside";
import { ReactComponent as CheckCircle } from '../media/check-circle.svg';
import { ReactComponent as AlertCircle } from '../media/alert-circle.svg';

type ContactFormProps = {
  handleClose: () => any;
};

export function ContactForm({ handleClose }: ContactFormProps) {
  const { t, i18n } = useTranslation(namespaces.common);
  const language = i18n.language;
  const containerRef = useRef<HTMLFormElement>(null);
  useClickOutside(containerRef, handleClose, true);

  const [checked, setChecked] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    message: "",
  })
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [sentSuccessfully, setSentSuccessfully] = useState<boolean>(false);

  const handleCheckboxChange = () => {
    setChecked(!checked);
  };

  const handleInputChange = useCallback((e) => {
    const newFormData = { ...formData, [e.target.name]: e.target.value }
    setFormData(newFormData)
  }, [formData])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    console.log({ formData })
    setLoading(true)

    try {
      const response = await fetch('https://www.vev-servizi.it/email.php', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ ...formData, language }).toString(),
      });
      console.log(response)

      if (response.ok) {
        setSentSuccessfully(true)
      } else {
        console.error('Failed to submit form');
      }
    } catch (error) {
      setError(true);
      console.error('Error:', error);
    } finally {
      // Set loading back to false regardless of the outcome
      setLoading(false);
    }
  }, [formData, language, setSentSuccessfully, setError, setLoading])

  return (
    <Dialog.Root open onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          inset: 0,
        }} />

        <Dialog.Content asChild>
          <Wrapper>
            <Dialog.Close asChild>
              <CloseButton aria-label={t("Chiudi")}>
                <X stroke="#728190" />
              </CloseButton>
            </Dialog.Close>
          <FormContainer onSubmit={handleSubmit} action="#" ref={containerRef}>
            {!sentSuccessfully && <>
              <Title>
                <Dialog.Title>{t("Richiedi un preventivo gratis e senza impegno")} </Dialog.Title>
              </Title>
              <InformationContainer>
                <Label htmlFor="firstname">{t("Nome *")}</Label>
                <div>
                  <Input type="text" id="firstname" name="firstname" value={formData.firstname} onChange={handleInputChange} disabled={loading} required />
                </div>
              </InformationContainer>
              <InformationContainer>
                <Label htmlFor="lastname">{t("Cognome *")}</Label>
                <div>
                  <Input type="text" id="lastname" name="lastname" value={formData.lastname} onChange={handleInputChange} disabled={loading} required />
                </div>
              </InformationContainer>
              <InformationContainer>
                <Label htmlFor="email">{t("E-mail *")}</Label>
                <div>
                  <Input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} disabled={loading} required />
                </div>
              </InformationContainer>
              <InformationContainer>
                <Label htmlFor="phone">{t("Telefono *")}</Label>
                <div>
                  <Input type="text" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} disabled={loading} required />
                </div>
              </InformationContainer>
              <MessageContainer>
                <Label htmlFor="message">{t("Messaggio *")}</Label>
                <div>
                  <MessageTextarea id="message" name="message" rows={6} value={formData.message} onChange={handleInputChange} disabled={loading} required />
                </div>
              </MessageContainer>
              <CheckboxContainer>
                  <Input id="conditions_checkbox" type="checkbox" checked={checked} onChange={handleCheckboxChange} disabled={loading} required />
                  <Label htmlFor="conditions_checkbox" style={{ flex: "1 0 calc(100% - 24px)" }}>
                    <Trans i18nKey="__Accetta_condizioni__">
                      Ho letto e accettato la <StyledLink to="/privacy-policy" target="_blank">Politica di riservatezza e le Condizioni d'uso</StyledLink>
                    </Trans>
                  </Label>
              </CheckboxContainer>
              <br />
              {loading && <GeneralText>{t("Caricamento...")}</GeneralText>}
              <ButtonContainer>
                <PrimaryButton
                  disabled={Object.values(formData).some(value => value === '') || !checked || loading}
                  type="submit"
                  $textColor="white"
                  $backgroundColor="var(--color-blue-gradient)"
                >{t("Richiedi preventivo")}</PrimaryButton>
              </ButtonContainer>
            </>}

            {sentSuccessfully && <Feedback>
                <IconContainer>
                    <CheckCircle stroke="var(--color-blue)" />
                </IconContainer>
                <TextContainer>
                  <GeneralText>{t("La tua richiesta di preventivo è stata inviata correttamente.")}</GeneralText>
                  <GeneralText><Trans i18nKey="__Mail_di_conferma__" email={formData.email}>
                    Abbiamo inviato la conferma a <InlineAnchor href={`mailto:${formData.email}`}>{{ email: formData.email }}</InlineAnchor>
                  </Trans></GeneralText>
                </TextContainer>
            </Feedback>}

            {error && <Feedback>
              <IconContainer>
                <AlertCircle stroke="var(--color-red)" />
              </IconContainer>
              <TextContainer>
                <GeneralText>{t("Ooups... qualcosa è andato storto.")}</GeneralText>
                <GeneralText><Trans i18nKey="__Errore__">
                  Riprova più tardi o contattaci direttamente all'indirizzo <InlineAnchor href="mailto:vevservizisrl@gmail.com">vevservizisrl@gmail.com</InlineAnchor>
                </Trans></GeneralText>
              </TextContainer>
            </Feedback>}

          </FormContainer>

        </Wrapper>
          </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
const Title = styled(StyledHeader)`
  font-size: 1.2rem;
`;
const Wrapper = styled.div`   
  background-color: var(--color-gray-transparent);
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  overflow-y: scroll;
  overflow-x: hidden;
`;
const FormContainer = styled.form`
  padding: 20px;
  background-color: white;
  color: var(--color-gray-dark);
  margin-bottom: 6em;
  height: max-content;
  grid-column: 3/-3;
  border-radius: 6px;
  box-shadow: 0 5px 15px rgba(0,0,0,.5);
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: max-content;
  flex-direction: column;
  
  @media (max-width: 950px) {
    grid-column: 2/-2;
  }
`;
const InformationContainer = styled.div`
  @media (max-width: 450px) {
    grid-column: span 2;
  }
`;
const CloseButton = styled.button<ButtonHTMLAttributes<HTMLButtonElement>>`
  grid-column: -4;
  justify-self: end;
  border: none;
  outline: inherit;
  cursor: pointer;
  width: 24px;
  padding: 0;
  margin: 6em 0 -15px 0;
  background-color: transparent;
  :hover {
      stroke: var(--color-gray-dark);
  }
  &:focus, &:focus-visible {
      border: medium none;
      outline: auto;
  }
  @media (max-width: 950px) {
      grid-column: -3;
  }
`;
const Input = styled.input<InputHTMLAttributes<HTMLInputElement>>`
  width: 100%;
  font-size: 1em;

  &[type="checkbox"] {
      width: max-content;
      line-height: 1em;
      flex: 1 0 20px;
      align-self: flex-start;
      height: 1em;
      margin-top: 2px;
  }   
`;
const Label = styled.label<LabelHTMLAttributes<HTMLLabelElement>>`
  font-size: 1em;
  display: inline;
`;
const MessageTextarea = styled.textarea<TextareaHTMLAttributes<HTMLTextAreaElement>>`
  width: 100%;
  font-size: 1em;
  resize: none;
  grid-column: span 2;
`;
const ButtonContainer = styled.div`
  align-self: end;
  justify-self: end;
  margin-top: 2em;
`;
const CheckboxContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  gap: 5px;
  align-items: baseline;
  grid-column: span 2;
`;
const StyledLink = styled(Link)`
  color: var(--color-blue);
  word-wrap: anywhere;
`;
const InlineAnchor = styled.a<AnchorHTMLAttributes<HTMLAnchorElement>>`
  color: var(--color-blue);
  word-wrap: anywhere;
`;
const MessageContainer = styled.div`
  grid-column: span 2;
`;
const Feedback = styled.div`
  grid-column: span 2;
  align-self: center;
  justify-self: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2em;
  min-height: 500px;
`;
const IconContainer = styled.div`
  height: 100px;
  width: 100px;
`;
const TextContainer = styled.div`
  text-align: center;
`;
